"use client";

import { Tooltip as BaseTooltip } from "@base-ui/react/tooltip";
import clsx from "clsx";
import React, { useContext } from "react";
import {
  FLOATING_POPUP_CLASS,
  getFloatingPopupStyle,
  getFloatingPortalProps,
  getFloatingPositionerProps,
} from "shared-web/components/common/FloatingLayer/FloatingLayer";
import { composeRefs } from "src/components/common/Composition";
import type {
  PopoverContentProps,
  PopoverProps,
  PopoverReferenceProps,
} from "src/components/common/Popover/Popover";
import PopoverContext, { useOverlayState } from "src/components/common/Popover/PopoverContext";
import { useVisibility } from "src/lib/Visibility";
import styles from "./Tooltip.module.scss";

/** A hover and focus tooltip. For something that opens on press, use `Popover`. */
export interface TooltipProps extends Omit<PopoverProps, "triggers" | "animated" | "overlay"> {}

interface TooltipInternalContextValue {
  hoverDelay: number;
  positionerProps: ReturnType<typeof getFloatingPositionerProps>;
}

const TooltipInternalContext = React.createContext<TooltipInternalContextValue | undefined>(
  undefined,
);

const Tooltip = (props: TooltipProps) => {
  const {
    children,
    visible,
    onVisibleChange,
    disabled,
    interactive = true,
    hoverDelay,
    handleRef,
    tags,
    ...positioning
  } = props;
  const { visible: contextVisible } = useVisibility();
  const { setVisible, context, onOpenChangeComplete } = useOverlayState({
    // A tooltip inside a hidden popover stays closed.
    visible: !contextVisible ? false : visible,
    onVisibleChange,
    handleRef,
    tags,
  });

  return (
    <TooltipInternalContext.Provider
      value={{
        hoverDelay: hoverDelay ?? 100,
        positionerProps: getFloatingPositionerProps({
          avoidReferenceOverlap: true,
          ...positioning,
        }),
      }}
    >
      <PopoverContext.Provider value={context}>
        <BaseTooltip.Root
          open={context.visible}
          disabled={disabled}
          disableHoverablePopup={!interactive}
          onOpenChangeComplete={onOpenChangeComplete}
          onOpenChange={(nextVisible, details) => {
            setVisible(nextVisible, details.event as Event | undefined, details.reason);
          }}
        >
          {typeof children === "function" ? children(context) : children}
        </BaseTooltip.Root>
      </PopoverContext.Provider>
    </TooltipInternalContext.Provider>
  );
};

export type TooltipTriggerProps<C extends React.ElementType = "button"> = PopoverReferenceProps<C>;

const TooltipTrigger = <C extends React.ElementType = "button">(props: TooltipTriggerProps<C>) => {
  const { asChild, as, ref, children, trigger: _, ...otherProps } = props;
  const internal = useContext(TooltipInternalContext);
  const popoverContext = useContext(PopoverContext);
  const Comp: any = as ?? "button";
  const render =
    asChild && React.isValidElement(children) ? (children as React.ReactElement) : <Comp />;
  return (
    <BaseTooltip.Trigger
      ref={composeRefs(ref, popoverContext?.refs.reference)}
      render={render}
      delay={internal?.hoverDelay}
      {...(otherProps as any)}
    >
      {asChild ? undefined : children}
    </BaseTooltip.Trigger>
  );
};

export type TooltipReferenceProps<C extends React.ElementType = "button"> =
  PopoverReferenceProps<C>;

const TooltipReference = <C extends React.ElementType = "button">(
  props: TooltipReferenceProps<C>,
) => <TooltipTrigger data-popover-reference={true} {...(props as any)} />;

export type TooltipContentProps = PopoverContentProps;

const TooltipContent = (props: TooltipContentProps) => {
  const { className, children, ref, style, asChild: _, initialFocus: __, ...otherProps } = props;
  const internal = useContext(TooltipInternalContext);
  const popoverContext = useContext(PopoverContext);

  if (!internal || !popoverContext) {
    return null;
  }

  return (
    <BaseTooltip.Portal {...getFloatingPortalProps(popoverContext.refs.reference.current)}>
      <BaseTooltip.Positioner {...internal.positionerProps}>
        <BaseTooltip.Popup
          ref={composeRefs(ref, popoverContext.refs.floating)}
          id={popoverContext.floatingId}
          data-tooltip={true}
          className={clsx(FLOATING_POPUP_CLASS, styles.tooltipContent, className)}
          style={{ ...getFloatingPopupStyle(), ...style }}
          {...(otherProps as any)}
        >
          {children}
        </BaseTooltip.Popup>
      </BaseTooltip.Positioner>
    </BaseTooltip.Portal>
  );
};

/** Tooltip appearance for libraries that manage their own popup positioning. */
const TooltipSurface = ({ className, ...props }: React.ComponentProps<"div">) => (
  <div className={clsx(styles.tooltipContent, className)} {...props} />
);

Tooltip.Trigger = TooltipTrigger;
Tooltip.Reference = TooltipReference;
Tooltip.Content = TooltipContent;
Tooltip.Surface = TooltipSurface;

export default Tooltip;
